import { useCallback, useState } from 'react'
import { PhoneInfoScreen } from './screens/basic-player-information/phone-player-information-screen'
import { SlotSelection } from './screens/bookable-selection/bookable-selection-screen'
import { REASON_FOR_VISIT } from './types'
import { ProgressStepper } from '@Components/progress-stepper/progress-stepper'

import './standard-claim-flow.scss'
import { ChooseLocationScreen } from './screens/choose-location/choose-location-screen'
import {
  ReasonForVisitData,
  ReasonForVisitScreen,
} from './screens/reason-for-visit/reason-for-visit-screen'
import { useMutation } from '@tanstack/react-query'
import { PlayerDataServiceAPI } from '@API/data-services/player-data-service'
import { useDispatch } from 'react-redux'
import { Header } from '@Components/header/header'
import { ClaimReservationActions } from '@AppState/claim-reservation'
import { useNavigate } from 'react-router-dom'
import { useHostConfig } from '../../hooks/use-host-config'
import { LoadingIndicator } from '@Components/loading-indicator/loading-indicator'
import { ClaimReservationCommunicationMethod } from '@interaction-gaming/player-data-service-browser-client'
import { VerificationScreen } from '@Components/claim-flow/screens/text-verification/verification-screen.tsx'
import { PATHS } from '../../constants'
import { EmailInfoScreen } from './screens/basic-player-information/email-player-information-screen'
import { PlayerInfoFormData } from './screens/basic-player-information/types'

enum FORM_STEPS {
  REASON_FOR_VISIT = 0,
  CHOOSE_LOCATION = 1,
  SLOT_SELECTION = 2,
  PLAYER_INFO = 3,
  VERIFICATION = 4,
  CLAIMING = 5,
}

export default function NewClaimTicketForm() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [step, setStep] = useState<FORM_STEPS>(FORM_STEPS.REASON_FOR_VISIT)

  const [numberOfTickets, setNumberOfTickets] = useState<number | undefined>(undefined)

  const [playerInfo, setPlayerInfo] = useState<PlayerInfoFormData | null>(null)

  const [locations, setLocations] = useState<string[]>([])

  const [validateStepErrorMessage, setValidateStepErrorMessage] = useState<string | undefined>()

  const hostConfig = useHostConfig()

  const [communicationMethod, setCommunicationMethod] =
    useState<ClaimReservationCommunicationMethod>(hostConfig.enabledCommunicationMethods[0])

  const TITLE_BY_STEP: Record<FORM_STEPS, string> = {
    [FORM_STEPS.REASON_FOR_VISIT]: 'Prize Amount',
    [FORM_STEPS.PLAYER_INFO]: 'Basic Info',
    [FORM_STEPS.VERIFICATION]:
      communicationMethod === ClaimReservationCommunicationMethod.PHONE
        ? 'Verify Phone Number'
        : 'Verify Email Address',
    [FORM_STEPS.CHOOSE_LOCATION]: 'Choose Location',
    [FORM_STEPS.SLOT_SELECTION]: hostConfig.bookableScreen.header,
    [FORM_STEPS.CLAIMING]: '',
  }

  const [selectedTimeslot, setSelectedSlot] = useState<string | null>(null)

  const { mutate: makeClaimReservation } = useMutation({
    mutationKey: [playerInfo, numberOfTickets, selectedTimeslot],
    mutationFn: async () => {
      dispatch(ClaimReservationActions.clearSavedReservation())

      return await PlayerDataServiceAPI.makeClaimReservation({
        notes: null,
        numberOfTickets: numberOfTickets!,
        phone: playerInfo!.phoneNumber,
        email: playerInfo!.emailAddress,
        name: `${playerInfo?.firstName} ${playerInfo?.lastName}`,
        timeslotId: selectedTimeslot!,
        communicationPreference: communicationMethod,
      })
    },
    onSuccess: (response) => {
      dispatch(ClaimReservationActions.setClaimReservation(response))
      navigate(`${PATHS.CONFIRMATION}${location.search}`)
    },
  })

  const reasonForVisitHandler = useCallback((data: ReasonForVisitData) => {
    setNumberOfTickets(data.numberOfTickets)
  }, [])

  const renderContents = useCallback(() => {
    switch (step) {
      case FORM_STEPS.REASON_FOR_VISIT:
        return (
          <ReasonForVisitScreen
            onNext={(data) => {
              if (data.reasonForVisit === REASON_FOR_VISIT.PROBLEM_WITH_TICKET) {
                navigate(`${PATHS.DAMAGED_TICKET}${location.search}`)
              } else if (data.reasonForVisit === REASON_FOR_VISIT.BIG_WINNER) {
                navigate(`${PATHS.BIG_WINNER}${location.search}`)
              } else if (data.reasonForVisit === REASON_FOR_VISIT.UNKNOWN_WIN) {
                navigate(`${PATHS.UNKNOWN}${location.search}`)
              } else if (data.reasonForVisit === REASON_FOR_VISIT.REGULAR_TICKET) {
                navigate(`${PATHS.REGULAR_TICKET}${location.search}`)
              } else if (data.numberOfTickets >= hostConfig.manyTicketsFlow.ticketThreshold) {
                navigate(`${PATHS.MANY_TICKETS}${location.search}`)
              }
              setCommunicationMethod(data.communicationMethod)
              reasonForVisitHandler(data)
              setStep(FORM_STEPS.CHOOSE_LOCATION)
            }}
          />
        )

      case FORM_STEPS.PLAYER_INFO:
        switch (communicationMethod) {
          case ClaimReservationCommunicationMethod.PHONE:
          default:
            return (
              <PhoneInfoScreen
                rescheduleFlow={false}
                formData={playerInfo ?? undefined}
                onSubmit={(data: PlayerInfoFormData) => {
                  setPlayerInfo(data)
                  PlayerDataServiceAPI.sendVerification(
                    data.phoneNumber,
                    data.emailAddress,
                    ClaimReservationCommunicationMethod.PHONE,
                  )
                  setStep(FORM_STEPS.VERIFICATION)
                }}
                onBack={() => setStep(FORM_STEPS.SLOT_SELECTION)}
              />
            )

          case ClaimReservationCommunicationMethod.EMAIL:
            return (
              <EmailInfoScreen
                rescheduleFlow={false}
                formData={playerInfo ?? undefined}
                onSubmit={(data: PlayerInfoFormData) => {
                  setPlayerInfo(data)
                  PlayerDataServiceAPI.sendVerification(
                    data.phoneNumber,
                    data.emailAddress,
                    communicationMethod,
                  )
                  setStep(FORM_STEPS.VERIFICATION)
                }}
                onBack={() => setStep(FORM_STEPS.SLOT_SELECTION)}
              />
            )
        }

      case FORM_STEPS.CHOOSE_LOCATION:
        return (
          <ChooseLocationScreen
            initialValue={[]}
            onNext={(val: string[]) => {
              setLocations(val)
              setStep(FORM_STEPS.SLOT_SELECTION)
            }}
            onBack={() => {
              setStep(FORM_STEPS.REASON_FOR_VISIT)
            }}
          />
        )

      case FORM_STEPS.SLOT_SELECTION:
        if (!numberOfTickets) {
          setStep(FORM_STEPS.REASON_FOR_VISIT)
          return null
        }
        return (
          <SlotSelection
            selectedCenters={locations}
            onSubmit={(slot: string) => {
              setSelectedSlot(slot)
              setStep(FORM_STEPS.PLAYER_INFO)
            }}
            onBack={() => {
              setLocations([])
              setStep(FORM_STEPS.CHOOSE_LOCATION)
            }}
            numberOfTickets={numberOfTickets}
          />
        )

      case FORM_STEPS.VERIFICATION:
        if (playerInfo?.emailAddress || playerInfo?.phoneNumber) {
          return (
            <VerificationScreen
              phoneNumber={playerInfo?.phoneNumber}
              emailAddress={playerInfo?.emailAddress}
              contactMethod={communicationMethod}
              errorMessage={validateStepErrorMessage}
              onBack={() => setStep(FORM_STEPS.PLAYER_INFO)}
              onNext={async (phoneNumber, emailAddress, contactMethod, code) => {
                try {
                  setValidateStepErrorMessage(undefined)
                  const validation = await PlayerDataServiceAPI.validateCode(
                    phoneNumber,
                    emailAddress,
                    contactMethod,
                    code,
                  )

                  if (validation.success) {
                    setStep(FORM_STEPS.CLAIMING)
                    makeClaimReservation()
                  } else {
                    setValidateStepErrorMessage('Invalid code')
                  }
                } catch (e) {
                  console.error('Error validating code', e)
                  setValidateStepErrorMessage(
                    'Error validating code, please try resending a new code',
                  )
                }
              }}
            />
          )
        } else {
          setStep(FORM_STEPS.PLAYER_INFO)
          return null
        }

      case FORM_STEPS.CLAIMING:
        return <LoadingIndicator active={true} />
    }
  }, [
    step,
    hostConfig.manyTicketsFlow.ticketThreshold,
    playerInfo,
    numberOfTickets,
    locations,
    reasonForVisitHandler,
    navigate,
    validateStepErrorMessage,
    makeClaimReservation,
    communicationMethod,
  ])

  const renderProgressStepper = useCallback(() => {
    let progressStep
    switch (step) {
      case FORM_STEPS.REASON_FOR_VISIT:
        progressStep = 0
        break
      case FORM_STEPS.CHOOSE_LOCATION:
        progressStep = 1
        break
      case FORM_STEPS.SLOT_SELECTION:
        progressStep = 2
        break
      case FORM_STEPS.PLAYER_INFO:
        progressStep = 3
        break
      case FORM_STEPS.VERIFICATION:
        progressStep = 3
        break
      case FORM_STEPS.CLAIMING:
        progressStep = 4
        break
    }

    return (
      <ProgressStepper
        steps={[{ label: 'Amount' }, { label: 'Location' }, { label: 'Date' }, { label: 'Info' }]}
        currentStepIndex={progressStep}
      />
    )
  }, [step])

  return (
    <>
      <Header title={'Schedule an Appointment'} />
      <section className="form">
        {renderProgressStepper()}
        <h2 className="form__title">{TITLE_BY_STEP[step]}</h2>
        <section className="form__contents">{renderContents()}</section>
      </section>
    </>
  )
}
